import React, { createContext, useContext, useMemo, useCallback } from 'react';
import { getSessionStorageValue, removeSessionStorageValue, setSessionStorageValue, STORAGE_KEYS } from '../../Utils/SessionStorage';

const CustomerContext = createContext();

export const CustomerProvider = ({ children }) => {
  const defaultFormData = {
    fname: '',
    lname: '',
    mobile: '',
    email: '',
    countryId: '',
    notes: '',
  };

  const [formData, setFormDataState] = React.useState(
    () => {
      const stored = getSessionStorageValue(STORAGE_KEYS.FORM_DATA);
      return stored && typeof stored === 'object' ? stored : defaultFormData;
    }
  );

  const [isUserVerified, setIsUserVerifiedState] = React.useState(
    () => getSessionStorageValue(STORAGE_KEYS.USER_VERIFIED) || false
  );

  const [verifiedMobile, setVerifiedMobileState] = React.useState(
    () => getSessionStorageValue(STORAGE_KEYS.VERIFIED_MOBILE) || ''
  );

  const [userID, setUserIDState] = React.useState(
    () => getSessionStorageValue(STORAGE_KEYS.USER_ID) || null
  );

  // Persist formData to sessionStorage whenever it changes
  React.useEffect(() => {
    if (formData && Object.values(formData).some((value) => value !== '' && value != null)) {
      setSessionStorageValue(STORAGE_KEYS.FORM_DATA, formData);
    } else {
      removeSessionStorageValue(STORAGE_KEYS.FORM_DATA);
    }
  }, [formData]);

  const setFormData = useCallback((data) => {
    if (typeof data === 'function') {
      setFormDataState((prev) => {
        const updated = data(prev);
        return (updated && typeof updated === 'object') ? updated : prev;
      });
    } else if (data && typeof data === 'object') {
      setFormDataState(data);
    }
  }, []);

  const setIsUserVerified = useCallback((verified) => {
    setIsUserVerifiedState(verified);
    setSessionStorageValue(STORAGE_KEYS.USER_VERIFIED, verified);
  }, []);

  const setVerifiedMobile = useCallback((mobile) => {
    const nextMobile = typeof mobile === 'string' ? mobile : '';
    setVerifiedMobileState(nextMobile);
    setSessionStorageValue(STORAGE_KEYS.VERIFIED_MOBILE, nextMobile);
  }, []);

  const setUserID = useCallback((id) => {
    setUserIDState(id);
    if (id === null) {
      setSessionStorageValue(STORAGE_KEYS.USER_ID, null);
    } else {
      setSessionStorageValue(STORAGE_KEYS.USER_ID, id);
    }
  }, []);

  const value = useMemo(
    () => ({
      formData,
      setFormData,
      isUserVerified,
      setIsUserVerified,
      verifiedMobile,
      setVerifiedMobile,
      userID,
      setUserID,
    }),
    [
      formData,
      setFormData,
      isUserVerified,
      setIsUserVerified,
      verifiedMobile,
      setVerifiedMobile,
      userID,
      setUserID,
    ]
  );

  return (
    <CustomerContext.Provider value={value}>
      {children}
    </CustomerContext.Provider>
  );
};

export const useCustomer = () => {
  const context = useContext(CustomerContext);
  if (!context) {
    throw new Error('useCustomer must be used within CustomerProvider');
  }
  return context;
};

export default CustomerContext;
